import React, { useDeferredValue, useEffect, useMemo, useState, useRef } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';
import { normalizeProductStatus } from '../utils/productStatus';
import {
  TrendingUp,
  DollarSign,
  Search,
  AlertTriangle,
  Layers,
  MapPin,
  Tag,
  RefreshCcw,
  Edit3,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  Download,
  UploadCloud,
  CheckSquare,
  Square,
  Wand2,
  BadgeAlert,
  CheckCircle2,
  History,
  User,
  FileText,
  Calendar,
  Check, 
  ChefHat,
  Package,
  ArrowLeft,
  Filter,
  ChevronDown, 
  ChevronUp,
  FlaskConical // ✅ NUEVO IMPORT PARA EL BOTON VER RECETA
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Helper de seguridad numérica (Evita errores 400 por NaN)
 */
const safeNum = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Normalizacion de product_type.
 * La tabla `products` recibe este campo desde varias vias (alta manual, importador
 * de Excel, ComboBuilder, POS) y no todas escriben el mismo string. Comparar por
 * igualdad exacta dejaba productos fuera de TODAS las vistas -> invisibles.
 */
const normalizeType = (t) =>
  String(t ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const RAW_TYPES = new Set(['materia prima', 'insumo', 'insumo / materia prima', 'insumos']);

/**
 * Un producto es "insumo" si su tipo lo dice; TODO lo demas (Producto Terminado,
 * Servicio, Combo, Misc, tipos importados desconocidos o vacios) se considera
 * vendible y cae en la vista de terminados. Mismo criterio que usan POS y Tienda,
 * que excluyen materias primas y muestran el resto. Asi ninguna fila queda huerfana.
 */
const isRawType = (t) => RAW_TYPES.has(normalizeType(t));
const matchesViewMode = (row, mode) => {
  if (mode === 'raw') return isRawType(row?.productType);
  if (mode === 'finished') return !isRawType(row?.productType) || Boolean(row?.hasRecipe);
  return true; // 'all'
};

/**
 * Helpers de formato
 */
const money = (n) =>
  safeNum(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pct = (n) => `${safeNum(n).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/**
 * HELPER DE FECHAS
 */
const getLocalDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFirstDayOfMonth = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * CSV para Excel (AR/ES)
 */
const toCSV = (rows, delimiter = ';') => {
  if (!rows || rows.length === 0) return '\ufeffSIN_DATOS';

  const headers = Object.keys(rows[0]);
  const esc = (val) => {
    const s = String(val ?? '');
    const needsQuotes = s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r');
    const safe = s.replaceAll('"', '""');
    return needsQuotes ? `"${safe}"` : safe;
  };

  const headerLine = headers.map((h) => esc(h)).join(delimiter);
  const body = rows
    .map((r) => headers.map((h) => esc(r[h])).join(delimiter))
    .join('\n');

  return `\ufeff${headerLine}\n${body}`;
};

/**
 * Timeout defensivo
 */
const withTimeout = (promise, ms = 20000, label = 'request') => {
  let t;
  const timeout = new Promise((_, reject) => {
    t = window.setTimeout(() => reject(new Error(`Timeout (${label}) > ${ms}ms`)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(t));
};

/**
 * Chunk helper (batch friendly)
 */
const chunk = (arr, size = 50) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const fetchAllBatches = async (buildQuery, pageSize = 500) => {
  const rows = [];
  let from = 0;

  while (true) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;

    const batch = Array.isArray(data) ? data : [];
    rows.push(...batch);

    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
};

// Traduce nombres técnicos a nombres amigables
const translateField = (field) => {
  switch (field) {
    case 'price': return 'Precio Venta';
    case 'cost_price': return 'Costo';
    case 'cost': return 'Costo';
    case 'target_margin': return 'Margen %';
    default: return field;
  }
};

// Formatea valores en el historial ($ o %)
const formatHistoryValue = (field, val) => {
  if (field === 'price' || field === 'cost_price' || field === 'cost') return `$ ${money(val)}`;
  if (field === 'target_margin') return pct(val);
  return val;
};

const COSTS_DRAFT_STORAGE_KEY = 'impulza:gestion-costos:draft:v1';

const getCostsDraftStorageKey = (orgId) => `${COSTS_DRAFT_STORAGE_KEY}:${orgId || 'anon'}`;

const readCostsDraft = (orgId) => {
  if (!orgId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(getCostsDraftStorageKey(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeCostsDraft = (orgId, payload) => {
  if (!orgId || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(getCostsDraftStorageKey(orgId), JSON.stringify(payload));
  } catch {
    // Si no entra en storage, seguimos trabajando en memoria.
  }
};

const clearCostsDraft = (orgId) => {
  if (!orgId || typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(getCostsDraftStorageKey(orgId));
  } catch {
    // Sin impacto funcional.
  }
};

export default function GestionCostos({ onNavigate }) {
  const { profile, organization } = useAuth();
  const orgId = profile?.organization_id;

  // ---------- Estado de carga / guardado ----------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ---------- Data principal ----------
  const [rows, setRows] = useState([]);
  const [originalData, setOriginalData] = useState({});
  const [sitios, setSitios] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [usdRate, setUsdRate] = useState(0);
  const [enableCostSync, setEnableCostSync] = useState(false); // ✅ NUEVO ESTADO PARA CASCADA
  const [allowZeroSellPrice, setAllowZeroSellPrice] = useState(false);

  // ---------- Vistas y Filtros ----------
  const [viewMode, setViewMode] = useState(null);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [filtroSitio, setFiltroSitio] = useState('global');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const categoryMenuRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);

  // ---------- Historial ----------
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filterSuccess, setFilterSuccess] = useState(false); 
  const [historyFilters, setHistoryFilters] = useState({
    dateFrom: getDefaultStartDate(), 
    dateTo: getLocalDate(),
    user: '',
    source: ''
  });

  // ---------- Modo edición / selección ----------
  const [isEditMode, setIsEditMode] = useState(false);
  const [rowEditModeId, setRowEditModeId] = useState(null);

  const [seleccionados, setSeleccionados] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // ---------- Sort ----------
  const [sortConfig, setSortConfig] = useState({ key: 'productName', direction: 'asc' });

  // ---------- KPI Panel ----------
  const [kpiPanel, setKpiPanel] = useState({ open: false, type: null });
  const restoredDraftRef = useRef(false);
  const restoredScrollRef = useRef(false);
  const pendingScrollYRef = useRef(0);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  // ---------- Lifecycle ----------
  useEffect(() => {
    if (!orgId) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!orgId || restoredDraftRef.current) return;

    const draft = readCostsDraft(orgId);
    restoredDraftRef.current = true;
    restoredScrollRef.current = false;
    pendingScrollYRef.current = Number.isFinite(Number(draft?.scrollY)) ? Math.max(0, Number(draft.scrollY)) : 0;
    if (!draft) return;

    setViewMode(
      draft.viewMode === 'finished' || draft.viewMode === 'raw' || draft.viewMode === 'all'
        ? draft.viewMode
        : null,
    );
    setMostrarArchivados(Boolean(draft.mostrarArchivados));
    setFiltroSitio(typeof draft.filtroSitio === 'string' && draft.filtroSitio ? draft.filtroSitio : 'global');
    setFiltroTipo(typeof draft.filtroTipo === 'string' ? draft.filtroTipo : '');
    setBrandFilter(typeof draft.brandFilter === 'string' ? draft.brandFilter : '');
    setSearchTerm(typeof draft.searchTerm === 'string' ? draft.searchTerm : '');
    setSelectedCategories(Array.isArray(draft.selectedCategories) ? draft.selectedCategories : []);
    setIsEditMode(Boolean(draft.isEditMode));
    setRowEditModeId(typeof draft.rowEditModeId === 'string' ? draft.rowEditModeId : null);
    setSeleccionados(Array.isArray(draft.seleccionados) ? draft.seleccionados : []);
    setExpandedId(typeof draft.expandedId === 'string' ? draft.expandedId : null);
    setPage(Number(draft.page) > 0 ? Number(draft.page) : 1);
    setPageSize([100, 200, 500].includes(Number(draft.pageSize)) ? Number(draft.pageSize) : 200);
    setSortConfig(
      draft.sortConfig?.key
        ? {
            key: draft.sortConfig.key,
            direction: draft.sortConfig.direction === 'desc' ? 'desc' : 'asc'
          }
        : { key: 'productName', direction: 'asc' }
    );
  }, [orgId]);

  useEffect(() => {
    if (showHistory && orgId) {
      fetchHistory(); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory]); 

  useEffect(() => {
    if (!orgId || loading || restoredScrollRef.current === true) return;

    const targetY = pendingScrollYRef.current;
    restoredScrollRef.current = true;
    if (!Number.isFinite(targetY) || targetY <= 0) return;

    let raf1 = 0;
    let raf2 = 0;
    let timeoutId = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
        timeoutId = window.setTimeout(() => {
          window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
        }, 120);
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timeoutId);
    };
  }, [orgId, loading, rows.length, page, pageSize, expandedId, viewMode, filtroSitio, filtroTipo, brandFilter, searchTerm, selectedCategories.length]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setShowCategoryMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Fetch consolidado
   */
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [resProducts, resVariants, resSitios, resRecetas, resOrg] = await Promise.all([
        // ✅ CÓDIGO ACTUALIZADO: Agregado auto_update_price
        fetchAllBatches(() =>
          supabase
            .from('products')
            .select(`
              id,
              name,
              brand,
              product_type,
              unit_of_measure,
              barcode,
              cost_price,
              sell_price,
              price_usd,
              priced_in_usd,
              target_margin,
              auto_update_price,
              active,
              is_active,
              category_id,
              product_categories:product_categories ( name ),
              stock_levels ( quantity, site_id )
            `)
            .eq('organization_id', orgId)
            // FIX: ya no filtramos archivados en la query. Se traen y se ocultan en
            // el cliente con un toggle, para poder avisar "hay N archivados que
            // coinciden con tu busqueda" en vez de mostrar la lista vacia.
            .order('name', { ascending: true })
        ),

        fetchAllBatches(() =>
          supabase
            .from('product_variants')
            .select('id, product_id, sku, price, cost_price, barcode')
            .eq('organization_id', orgId)
            .order('product_id', { ascending: true })
            .order('id', { ascending: true })
        ),

        supabase
          .from('sitios')
          .select('id, nombre')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('nombre'),

        // ✅ CÓDIGO ACTUALIZADO: Select * para traer ingredientes
        supabase
          .from('recetas')
          .select('*')
          .eq('organization_id', orgId),
          
        // organization viene del AuthContext: evitamos el fetch redundante.
        Promise.resolve({ data: organization, error: null })
      ]);

      if (resRecetas.error) throw resRecetas.error;

      setEnableCostSync(resOrg.data?.enable_cost_sync || false);
      setUsdRate(safeNum(resOrg.data?.usd_exchange_rate));
      setAllowZeroSellPrice(Boolean(resOrg.data?.allow_zero_sell_price));
      setRecetas(resRecetas.data || []);

      const variantByProductId = new Map();
      (resVariants || []).forEach((v) => {
        if (!v.product_id) return;
        if (!variantByProductId.has(v.product_id)) variantByProductId.set(v.product_id, v);
      });

      const recipeProductIds = new Set((resRecetas.data || []).map(r => r.producto_id));
      const recipeByProductId = new Map((resRecetas.data || []).map((r) => [r.producto_id, r]));
      const draft = readCostsDraft(orgId);
      const draftRows = draft?.draftRows && typeof draft.draftRows === 'object' ? draft.draftRows : {};

      const unified = (resProducts || []).map((p) => {
        const v = variantByProductId.get(p.id) || null;
        const recipe = recipeByProductId.get(p.id);
        const recipeCost =
          recipe && Array.isArray(recipe.ingredientes)
            ? recipe.ingredientes.reduce(
                (acc, ing) => acc + (safeNum(ing?.costo_unitario) * safeNum(ing?.quantity)),
                0
              ) / Math.max(safeNum(recipe?.rendimiento), 1)
            : null;
        const cost = safeNum(recipeCost ?? p.cost_price ?? v?.cost_price);
        // Solo aplicamos la conversión USD → ARS si el producto tiene el flag explícito.
        const pricedInUsd = p.priced_in_usd === true;
        const price = safeNum(
          pricedInUsd && safeNum(p.price_usd) > 0 && safeNum(resOrg.data?.usd_exchange_rate) > 0
            ? safeNum(p.price_usd) * safeNum(resOrg.data?.usd_exchange_rate)
            : (p.sell_price ?? v?.price)
        );

        return {
          product_id: p.id,
          productName: p.name || '',
          brand: normalizeBrandLabel(p.brand),
          brandKey: normalizeBrandKey(p.brand),
          productType: p.product_type || 'Producto Terminado',
          archivado: normalizeProductStatus(p).active === false,
          uom: p.unit_of_measure || 'un',
          categoryName: p.product_categories?.name || 'Sin categoría',
          product_barcode: p.barcode || '',
          stock_levels: p.stock_levels || [],
          target_margin: safeNum(p.target_margin),
          price_usd: safeNum(p.price_usd),
          priced_in_usd: pricedInUsd,
          auto_update_price: p.auto_update_price, // ✅ GUARDADO EN MEMORIA

          variant_id: v?.id || null,
          sku: v?.sku || '',
          variant_barcode: v?.barcode || '',

          cost_price: cost,
          price: price,
          hasRecipe: recipeProductIds.has(p.id)
        };
      });

      const snapshot = {};
      unified.forEach(r => {
        snapshot[r.product_id] = { 
          cost_price: r.cost_price, 
          price: r.price, 
          target_margin: r.target_margin 
        };
      });
      setOriginalData(snapshot);

      setRows(
        unified.map((row) => {
          const draftRow = draftRows[row.product_id];
          if (!draftRow) return row;
          return {
            ...row,
            cost_price: draftRow.cost_price !== undefined ? safeNum(draftRow.cost_price) : row.cost_price,
            target_margin: draftRow.target_margin !== undefined ? safeNum(draftRow.target_margin) : row.target_margin,
            price: draftRow.price !== undefined ? safeNum(draftRow.price) : row.price
          };
        })
      );
      setSitios(resSitios.data || []);
    } catch (e) {
      console.error('[GestionCostos] fetchAll:', e);
      toast.error('Error al cargar Costos');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setFilterSuccess(false); 
    try {
      let query = supabase
        .from('status_history')
        .select('*') 
        .eq('organization_id', orgId)
        .in('field_name', ['price', 'cost_price', 'target_margin', 'cost'])
        .gte('changed_at', `${historyFilters.dateFrom}T00:00:00`)
        .lte('changed_at', `${historyFilters.dateTo}T23:59:59`)
        .order('changed_at', { ascending: false });

      const { data: logs, error } = await query;
      if (error) throw error;

      const userIds = [...new Set(logs.map(l => l.changed_by).filter(Boolean))];
      let profilesMap = {};

      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
           .from('profiles')
           .select('user_id, full_name, email')
           .in('user_id', userIds);
        
        if (profilesData) {
          profilesData.forEach(p => {
            profilesMap[p.user_id] = p;
          });
        }
      }

      const productMap = new Map();
      rows.forEach(r => {
        productMap.set(r.product_id, r.productName);
        if (r.variant_id) productMap.set(r.variant_id, r.productName);
      });

      const enrichedLogs = logs.map(log => {
        const metaSource = log.metadata?.source || 'Desconocido';
        if (historyFilters.source && !metaSource.toLowerCase().includes(historyFilters.source.toLowerCase())) return null;
        
        const profileInfo = profilesMap[log.changed_by];
        const userName = profileInfo?.full_name || profileInfo?.email || 'Sistema';

        if (historyFilters.user && !userName.toLowerCase().includes(historyFilters.user.toLowerCase())) return null;
        const prodName = productMap.get(log.entity_id) || 'Producto/Var ID: ' + log.entity_id.slice(0, 8);

        return { ...log, productName: prodName, userName, source: metaSource };
      }).filter(Boolean);

      setHistoryData(enrichedLogs);
      
      setFilterSuccess(true);
      setTimeout(() => setFilterSuccess(false), 2000); 

    } catch (e) {
      console.error(e);
      toast.error('Error cargando historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const registrarHistoriaCambio = async (entityId, field, oldVal, newVal, type = 'product') => {
    try {
      if (!orgId || !profile?.user_id) return;
      await supabase.from('status_history').insert({
        organization_id: orgId,
        entity_type: type === 'product' ? 'products' : 'product_variants',
        entity_id: entityId,
        field_name: field,
        from_value: String(oldVal),
        to_value: String(newVal),
        changed_by: profile.user_id,
        metadata: { source: 'Manual', module: 'GestionCostos' }
      });
    } catch (e) { console.warn("Error logging history", e); }
  };

  // ✅ NUEVO: FUNCIÓN PARA SINCRONIZACIÓN EN CASCADA
  const cascadeCosts = async (freshCostMap) => {
      if (!enableCostSync || recetas.length === 0 || Object.keys(freshCostMap).length === 0) return;

      try {
          const allProductsMap = {};
          rows.forEach(r => {
              allProductsMap[r.product_id] = {
                  cost_price: r.cost_price,
                  target_margin: r.target_margin,
                  auto_update_price: r.auto_update_price,
                  price: r.price
              };
          });

          // Sobre-escribimos con los precios frescos recién guardados
          for (const [pid, newCost] of Object.entries(freshCostMap)) {
              if (allProductsMap[pid]) {
                  allProductsMap[pid].cost_price = newCost;
              } else {
                  allProductsMap[pid] = { cost_price: newCost };
              }
          }

          let updatedCount = 0;

          // Revisamos recetas afectadas
          for (const rec of recetas) {
              const prodFinal = allProductsMap[rec.producto_id];
              if (!prodFinal) continue;

              const usesUpdatedIng = (rec.ingredientes || []).some(ing => freshCostMap[ing.id] !== undefined);
              if (!usesUpdatedIng) continue;

              let costoTotalMezcla = 0;
              (rec.ingredientes || []).forEach(ing => {
                  const costoFresco = allProductsMap[ing.id] !== undefined ? allProductsMap[ing.id].cost_price : Number(ing.costo_unitario || 0);
                  costoTotalMezcla += (costoFresco * Number(ing.quantity || 0));
              });

              const rend = Number(rec.rendimiento) || 1;
              const nuevoCostoUnitario = rend > 0 ? (costoTotalMezcla / rend) : 0;
              const nuevoCostoRedondeado = Number(nuevoCostoUnitario.toFixed(2));
              const costoActual = Number(prodFinal.cost_price || 0);

              if (Math.abs(nuevoCostoRedondeado - costoActual) > 0.01) {
                  let nuevoPrecioVenta = undefined;
                  
                  if (prodFinal.auto_update_price) {
                      const margin = Number(prodFinal.target_margin || 0);
                      nuevoPrecioVenta = Number((nuevoCostoRedondeado * (1 + (margin / 100))).toFixed(2));
                  }

                  const upPayload = { cost_price: nuevoCostoRedondeado };
                  if (nuevoPrecioVenta !== undefined) upPayload.sell_price = nuevoPrecioVenta;

                  await supabase.from('products').update(upPayload).eq('id', rec.producto_id);

                  const varPayload = { cost_price: nuevoCostoRedondeado };
                  if (nuevoPrecioVenta !== undefined) varPayload.price = nuevoPrecioVenta;
                  await supabase.from('product_variants').update(varPayload).eq('product_id', rec.producto_id);
                  
                  allProductsMap[rec.producto_id].cost_price = nuevoCostoRedondeado;
                  if (nuevoPrecioVenta !== undefined) allProductsMap[rec.producto_id].price = nuevoPrecioVenta;
                  
                  // Agregamos a freshCostMap por si existe una sub-receta encadenada
                  freshCostMap[rec.producto_id] = nuevoCostoRedondeado;

                  updatedCount++;
              }
          }
          if (updatedCount > 0) {
             toast.success(`Cascada: Se actualizaron ${updatedCount} producto(s) derivado(s).`);
          }
      } catch (err) {
          console.error("Error silencioso en cascada de costos:", err);
      }
  };

  const categoriesList = useMemo(() => {
    const cats = new Set();
    rows.forEach(r => {
      if (r.categoryName) cats.add(r.categoryName);
    });
    return Array.from(cats).sort();
  }, [rows]);

  const persistedDraftRows = useMemo(() => {
    const next = {};

    rows.forEach((row) => {
      const original = originalData[row.product_id];
      if (!original) return;

      const draft = {};
      if (safeNum(row.cost_price) !== safeNum(original.cost_price)) draft.cost_price = safeNum(row.cost_price);
      if (safeNum(row.target_margin) !== safeNum(original.target_margin)) draft.target_margin = safeNum(row.target_margin);
      if (safeNum(row.price) !== safeNum(original.price)) draft.price = safeNum(row.price);

      if (Object.keys(draft).length > 0) {
        next[row.product_id] = draft;
      }
    });

    return next;
  }, [rows, originalData]);

  const persistedUiDraft = useMemo(() => ({
    viewMode,
    mostrarArchivados,
    filtroSitio,
    filtroTipo,
    brandFilter,
    searchTerm,
    selectedCategories,
    isEditMode,
    rowEditModeId,
    seleccionados,
    expandedId,
    page,
    pageSize,
    sortConfig,
    draftRows: persistedDraftRows,
    scrollY: typeof window !== 'undefined' ? Math.max(0, window.scrollY || 0) : 0
  }), [
    viewMode,
    mostrarArchivados,
    filtroSitio,
    filtroTipo,
    brandFilter,
    searchTerm,
    selectedCategories,
    isEditMode,
    rowEditModeId,
    seleccionados,
    expandedId,
    page,
    pageSize,
    sortConfig,
    persistedDraftRows
  ]);

  useEffect(() => {
    if (!orgId || !restoredDraftRef.current) return;

    const currentScrollY = typeof window !== 'undefined' ? Math.max(0, window.scrollY || 0) : 0;

    const hasUiDraft =
      Boolean(viewMode) ||
      filtroSitio !== 'global' ||
      Boolean(filtroTipo) ||
      Boolean(brandFilter) ||
      Boolean(searchTerm) ||
      selectedCategories.length > 0 ||
      isEditMode ||
      rowEditModeId !== null ||
      seleccionados.length > 0 ||
      expandedId !== null ||
      sortConfig.key !== 'productName' ||
      sortConfig.direction !== 'asc' ||
      currentScrollY > 8;

    if (!hasUiDraft && Object.keys(persistedDraftRows).length === 0) {
      clearCostsDraft(orgId);
      return;
    }

    writeCostsDraft(orgId, persistedUiDraft);
  }, [orgId, persistedUiDraft]);

  useEffect(() => {
    if (!orgId || !restoredDraftRef.current) return;

    const persistCurrentScroll = () => {
      const existingDraft = readCostsDraft(orgId) || {};
      writeCostsDraft(orgId, {
        ...existingDraft,
        ...persistedUiDraft,
        scrollY: typeof window !== 'undefined' ? Math.max(0, window.scrollY || 0) : 0
      });
    };

    window.addEventListener('beforeunload', persistCurrentScroll);
    window.addEventListener('pagehide', persistCurrentScroll);

    return () => {
      window.removeEventListener('beforeunload', persistCurrentScroll);
      window.removeEventListener('pagehide', persistCurrentScroll);
    };
  }, [orgId, persistedUiDraft]);

  const computed = useMemo(() => {
    return rows.map((r) => {
      const levels = r.stock_levels || [];
      let stock = 0;
      if (filtroSitio === 'global') {
        stock = levels.reduce((acc, l) => acc + safeNum(l.quantity), 0);
      } else {
        const lvl = levels.find((l) => l.site_id === filtroSitio);
        stock = safeNum(lvl?.quantity);
      }

      const cost = r.cost_price;
      const price = r.price;
      const mk = r.target_margin;
      const suggested = cost > 0 ? cost * (1 + mk / 100) : 0;
      const mgReal = price > 0 ? ((price - cost) / price) * 100 : 0;

      const flags = {
        stockNeg: stock < 0,
        stockCero: stock === 0,
        costFaltante: cost <= 0,
        margenNegativo: price > 0 && cost > price
      };

      return { ...r, currentStock: stock, suggestedPrice: suggested, mgReal, flags };
    });
  }, [rows, filtroSitio]);

  const brandOptions = useMemo(() => buildBrandOptions(rows, (row) => row.brand), [rows]);

  // Las opciones de tipo salen de los datos reales, no de una lista fija: si una
  // organizacion tiene 'Combo' o un tipo importado propio, tiene que poder filtrarlo.
  const productTypeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.productType).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const filtered = useMemo(() => {
    const s = (deferredSearchTerm || '').trim().toLowerCase();

    return computed.filter((r) => {
      const matchSearch =
        !s ||
        (r.productName || '').toLowerCase().includes(s) ||
        (r.brand || '').toLowerCase().includes(s) ||
        (r.sku || '').toLowerCase().includes(s) ||
        String(r.product_barcode || r.variant_barcode || '').toLowerCase().includes(s);

      const matchTipo = !filtroTipo || r.productType === filtroTipo;
      const matchBrand = !brandFilter || r.brandKey === brandFilter;
      const matchCategory = selectedCategories.length === 0 || selectedCategories.includes(r.categoryName);

      const matchView = matchesViewMode(r, viewMode);
      const matchArchivado = mostrarArchivados || !r.archivado;

      return matchSearch && matchTipo && matchBrand && matchCategory && matchView && matchArchivado;
    });
  }, [computed, deferredSearchTerm, filtroTipo, brandFilter, selectedCategories, viewMode, mostrarArchivados]);

  // Cuantos archivados quedan ocultos con los filtros actuales (para el aviso).
  const archivadosOcultos = useMemo(() => {
    if (mostrarArchivados) return 0;
    const s = (deferredSearchTerm || '').trim().toLowerCase();

    return computed.filter((r) => {
      if (!r.archivado) return false;
      const matchSearch =
        !s ||
        (r.productName || '').toLowerCase().includes(s) ||
        (r.brand || '').toLowerCase().includes(s) ||
        (r.sku || '').toLowerCase().includes(s) ||
        String(r.product_barcode || r.variant_barcode || '').toLowerCase().includes(s);
      const matchTipo = !filtroTipo || r.productType === filtroTipo;
      const matchBrand = !brandFilter || r.brandKey === brandFilter;
      const matchCategory = selectedCategories.length === 0 || selectedCategories.includes(r.categoryName);
      return matchSearch && matchTipo && matchBrand && matchCategory && matchesViewMode(r, viewMode);
    }).length;
  }, [computed, deferredSearchTerm, filtroTipo, brandFilter, selectedCategories, viewMode, mostrarArchivados]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, direction } = sortConfig;
    arr.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        const as = String(av ?? '').toLowerCase();
        const bs = String(bv ?? '').toLowerCase();
        return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
      }
      const an = safeNum(av);
      const bn = safeNum(bv);
      return direction === 'asc' ? an - bn : bn - an;
    });
    return arr;
  }, [filtered, sortConfig]);

  const stats = useMemo(() => {
    const base = seleccionados.length ? sorted.filter((r) => seleccionados.includes(r.product_id)) : sorted;
    const valorizacion = base.reduce((acc, r) => acc + safeNum(r.cost_price) * safeNum(r.currentStock), 0);
    const avgMgReal = base.length ? base.reduce((acc, r) => acc + safeNum(r.mgReal), 0) / base.length : 0;
    
    const sinCosto = base.filter((r) => r.flags.costFaltante).length; 
    
    const alertasRent = base.filter((r) => r.flags.margenNegativo || r.flags.costFaltante || r.mgReal < 5).length;
    return { valorizacion, avgMgReal, sinCosto, alertasRent };
  }, [sorted, seleccionados]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    setPage(1);
  }, [deferredSearchTerm, filtroTipo, brandFilter, filtroSitio, selectedCategories, viewMode, sortConfig, pageSize]);

  // --- Actions ---
  const handleSort = (key) => {
    setSortConfig((prev) => {
        if (prev.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        const defaultDescKeys = ['currentStock', 'cost_price', 'target_margin', 'suggestedPrice', 'price', 'mgReal'];
        return { key, direction: defaultDescKeys.includes(key) ? 'desc' : 'asc' };
    });
  };

  const toggleSelect = (productId) => setSeleccionados((prev) => (prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]));
  const toggleSelectAll = () => (seleccionados.length === sorted.length ? setSeleccionados([]) : setSeleccionados(sorted.map((i) => i.product_id)));

  const handleInputChange = (productId, field, newValue) => {
    const val = parseFloat(newValue);
    setRows((prev) =>
      prev.map((r) => {
        if (r.product_id !== productId) return r;
        if (field === 'target_margin') return { ...r, target_margin: val };
        if (field === 'cost_price') return { ...r, cost_price: val };
        if (field === 'price') return { ...r, price: val };
        return r;
      })
    );
  };

  const requiresPositiveSellPrice = (row) =>
    !allowZeroSellPrice && !isRawType(row?.productType);

  const findZeroSellPriceRow = (rowsToValidate = []) =>
    rowsToValidate.find((row) => requiresPositiveSellPrice(row) && safeNum(row?.price) <= 0) || null;

  const saveOne = async (row) => {
    if (saving) return;
    if (!row?.product_id) {
      toast.error('No se pudo identificar el producto');
      return;
    }
    if (requiresPositiveSellPrice(row) && safeNum(row.price) <= 0) {
      toast.error(`"${row.productName || 'Producto'}" necesita un precio de venta mayor a 0`);
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Guardando...');
    try {
      const pRes = await withTimeout(
        supabase
          .from('products')
          .update({
            target_margin: safeNum(row.target_margin),
            cost_price: safeNum(row.cost_price),
            sell_price: safeNum(row.price),
            // Solo recalculamos price_usd a partir del precio ARS si el producto está
            // explícitamente marcado en USD. Si no, dejamos el valor como estaba.
            price_usd:
              row.priced_in_usd === true && safeNum(usdRate) > 0
                ? Number((safeNum(row.price) / safeNum(usdRate)).toFixed(2))
                : safeNum(row.price_usd)
          })
          .eq('id', row.product_id)
          .eq('organization_id', orgId),
        20000,
        'update products'
      );
      if (pRes.error) throw pRes.error;

      if (row.variant_id) {
        const vRes = await withTimeout(
          supabase
            .from('product_variants')
            .update({
              price: safeNum(row.price),
              cost_price: safeNum(row.cost_price)
            })
            .eq('id', row.variant_id)
            .eq('organization_id', orgId),
          20000,
          'update variants'
        );
        if (vRes.error) throw vRes.error;

        const orig = originalData[row.product_id] || {};
        const entityId = row.variant_id;
        
        if (safeNum(orig.price) !== safeNum(row.price)) {
          await registrarHistoriaCambio(entityId, 'price', orig.price || '0', row.price, 'variant');
        }
        if (safeNum(orig.cost_price) !== safeNum(row.cost_price)) {
          await registrarHistoriaCambio(entityId, 'cost_price', orig.cost_price || '0', row.cost_price, 'variant');
        }
        if (safeNum(orig.target_margin) !== safeNum(row.target_margin)) {
          await registrarHistoriaCambio(entityId, 'target_margin', orig.target_margin || '0', row.target_margin, 'product');
        }

      } else {
        const ins = await withTimeout(
          supabase
            .from('product_variants')
            .insert({
              organization_id: orgId,
              product_id: row.product_id,
              price: safeNum(row.price),
              cost_price: safeNum(row.cost_price),
              sku: null,
              barcode: row.product_barcode || null,
              stock_quantity: 0
            })
            .select('id')
            .single(),
          20000,
          'insert variants'
        );

        if (ins.error) throw ins.error;
        if (ins.data?.id) await registrarHistoriaCambio(ins.data.id, 'price', '0', row.price, 'variant');
      }

      toast.success('Actualizado', { id: toastId });
      
      // ✅ CASCADA INDIVIDUAL
      if (safeNum(originalData[row.product_id]?.cost_price) !== safeNum(row.cost_price)) {
          await cascadeCosts({ [row.product_id]: safeNum(row.cost_price) });
      }

      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Error al guardar', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    if (saving) return;
    if (!orgId) {
      toast.error('Sin organización activa');
      return;
    }

    const base = seleccionados.length ? rows.filter((r) => seleccionados.includes(r.product_id)) : rows;
    const invalidRow = findZeroSellPriceRow(base);
    if (invalidRow) {
      toast.error(`"${invalidRow.productName || 'Producto'}" necesita un precio de venta mayor a 0`);
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    const total = base.length;

    const toastId = toast.loading(`Guardando cambios (${total})...`);

    try {
      const productsPayload = base
        .filter(r => r && r.product_id)
        .map((r) => ({
          id: r.product_id,
          organization_id: orgId,
          name: r.productName, 
          target_margin: safeNum(r.target_margin),
          cost_price: safeNum(r.cost_price),
          sell_price: safeNum(r.price),
          // Solo recalculamos price_usd cuando el producto está marcado en USD.
          price_usd:
            r.priced_in_usd === true && safeNum(usdRate) > 0
              ? Number((safeNum(r.price) / safeNum(usdRate)).toFixed(2))
              : safeNum(r.price_usd),
        }));

      for (const part of chunk(productsPayload, 50)) {
        if(part.length === 0) continue;
        const pRes = await withTimeout(
          supabase
            .from('products')
            .upsert(part, { onConflict: 'id' }),
          30000,
          'upsert products batch'
        );
        if (pRes?.error) throw pRes.error;
      }

      const variantsUpdatePayload = base
        .filter(r => r && r.variant_id)
        .map((r) => ({
          id: r.variant_id,
          organization_id: orgId,
          product_id: r.product_id,
          price: safeNum(r.price),
          cost_price: safeNum(r.cost_price),
        }));

      for (const part of chunk(variantsUpdatePayload, 50)) {
        if(part.length === 0) continue;
        const vRes = await withTimeout(
          supabase
            .from('product_variants')
            .upsert(part, { onConflict: 'id' }),
          30000,
          'upsert variants batch'
        );
        if (vRes?.error) throw vRes.error;
      }

      const variantsInsertPayload = base
        .filter(r => r && !r.variant_id && r.product_id)
        .map((r) => ({
          organization_id: orgId,
          product_id: r.product_id,
          price: safeNum(r.price),
          cost_price: safeNum(r.cost_price),
          sku: null,
          barcode: r.product_barcode || null,
          stock_quantity: 0,
        }));

      let insertedVariants = [];
      for (const part of chunk(variantsInsertPayload, 50)) {
        if(part.length === 0) continue;
        const ins = await withTimeout(
          supabase
            .from('product_variants')
            .insert(part)
            .select('id, product_id'),
          30000,
          'insert variants batch'
        );
        if (ins?.error) throw ins.error;
        insertedVariants = insertedVariants.concat(ins?.data || []);
      }

      if (profile?.user_id) {
        const now = new Date().toISOString();
        const historyPayload = [];

        for (const r of base) {
          const variantId = r.variant_id
            ? r.variant_id
            : (insertedVariants.find(x => x.product_id === r.product_id)?.id || null);

          if (!variantId) continue;

          const entityId = variantId; 
          const entityType = 'product_variants'; 

          const orig = originalData[r.product_id] || {};
          
          const oldPrice = safeNum(orig.price);
          const newPrice = safeNum(r.price);
          const oldCost = safeNum(orig.cost_price);
          const newCost = safeNum(r.cost_price);
          const oldMargin = safeNum(orig.target_margin);
          const newMargin = safeNum(r.target_margin);
          
          if (oldPrice !== newPrice) {
             historyPayload.push({
              organization_id: orgId,
              entity_type: entityType,
              entity_id: entityId,
              field_name: 'price',
              from_value: String(oldPrice),
              to_value: String(newPrice),
              changed_by: profile.user_id,
              changed_at: now,
              metadata: { source: 'Manual', module: 'GestionCostos', bulk: true },
            });
          }

          if (oldCost !== newCost) {
            historyPayload.push({
             organization_id: orgId,
             entity_type: entityType,
             entity_id: entityId,
             field_name: 'cost_price',
             from_value: String(oldCost),
             to_value: String(newCost),
             changed_by: profile.user_id,
             changed_at: now,
             metadata: { source: 'Manual', module: 'GestionCostos', bulk: true },
           });
         }

         if (oldMargin !== newMargin) {
            historyPayload.push({
             organization_id: orgId,
             entity_type: entityType,
             entity_id: entityId,
             field_name: 'target_margin',
             from_value: String(oldMargin),
             to_value: String(newMargin),
             changed_by: profile.user_id,
             changed_at: now,
             metadata: { source: 'Manual', module: 'GestionCostos', bulk: true },
           });
         }
        }

        if (historyPayload.length > 0) {
          for (const part of chunk(historyPayload, 100)) {
            await withTimeout(
              supabase.from('status_history').insert(part),
              30000,
              'insert history batch'
            );
          }
        }
      }

      toast.success('Cambios guardados', { id: toastId });
      
      // ✅ CASCADA MASIVA
      const changedCosts = {};
      for (const r of base) {
          const orig = originalData[r.product_id] || {};
          if (safeNum(orig.cost_price) !== safeNum(r.cost_price)) {
              changedCosts[r.product_id] = safeNum(r.cost_price);
          }
      }
      if (Object.keys(changedCosts).length > 0) {
          await cascadeCosts(changedCosts);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); 

      setIsEditMode(false);
      setRowEditModeId(null); 
      setSeleccionados([]);
      setExpandedId(null);
      setKpiPanel({ open: false, type: null });

      await fetchAll();
    } catch (e) {
      console.error('[GestionCostos] saveBulk batch error:', e);
      toast.error(`Error al guardar: ${e?.message || 'Error'}`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    const hasFilters =
      (searchTerm || '').trim().length > 0 ||
      (filtroTipo || '').trim().length > 0 ||
      filtroSitio !== 'global' ||
      selectedCategories.length > 0;

    const exportBase = hasFilters
      ? sorted
      : computed.filter((r) => matchesViewMode(r, viewMode) && (mostrarArchivados || !r.archivado));

    const base = seleccionados.length ? exportBase.filter((r) => seleccionados.includes(r.product_id)) : exportBase;
    const sitioLabel = filtroSitio === 'global' ? 'GLOBAL' : (sitios.find((s) => s.id === filtroSitio)?.nombre || 'SITIO');

    const rowsCSV = base.map((r) => ({
      PRODUCTO: r.productName,
      CATEGORIA: r.categoryName,
      SKU: r.sku || '',
      BARRAS: r.product_barcode || r.variant_barcode || '',
      TIPO: r.productType,
      ESTADO: r.archivado ? 'ARCHIVADO' : 'ACTIVO',
      SITIO: sitioLabel,
      STOCK: safeNum(r.currentStock),
      COSTO_UNIT: safeNum(r.cost_price),
      MG_EST: safeNum(r.target_margin),
      SUGERIDO: safeNum(r.suggestedPrice),
      VENTA: safeNum(r.price),
      MG_REAL: safeNum(r.mgReal)
    }));

    const csv = toCSV(rowsCSV, ';');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${BRAND_NAME}_Costos_${viewMode}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportado');
  };

  const SortableTh = ({ label, sortKey, align = 'left', className = '' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
        <th 
            className={`px-4 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
                <div className="leading-tight text-inherit">{label}</div>
                <div className="flex flex-col">
                    {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
                    {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
                    {!isActive && <div className="h-2.5 w-2.5 opacity-0 group-hover:opacity-30"><Filter size={10} /></div>}
                </div>
            </div>
        </th>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#F2F9FF] p-8" translate="no">
        <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
          <RefreshCcw className="text-slate-300 animate-spin" size={18} />
        </div>
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Costos…</p>
      </div>
    );
  }

  if (!viewMode) {
    return (
      <div className="w-full h-full p-6 animate-nova-in flex flex-col items-center justify-center bg-[#F2F9FF]">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black text-[#0B2A36] uppercase tracking-tight mb-2">Gestión de Costos</h1>
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Seleccione el tipo de producto a gestionar</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
          <button
            onClick={() => setViewMode('finished')}
            className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all group text-left relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-500"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-[#0B2A36] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:rotate-12 transition-transform">
                <ChefHat size={32} className="text-[#3FF2AA]" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight mb-2">Productos Terminados</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed">
                Gestionar precios de venta, márgenes y costos derivados.
                <br /><span className="text-blue-500 font-bold text-xs mt-2 block">Ideal para actualizar carta y rentabilidad.</span>
              </p>
            </div>
          </button>

          <button
            onClick={() => setViewMode('raw')}
            className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all group text-left relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-full -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-500"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:-rotate-12 transition-transform">
                <Package size={32} className="text-amber-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight mb-2">Materias Primas</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed">
                Actualizar costos de insumos, stock de depósito y compras.
                <br /><span className="text-amber-600 font-bold text-xs mt-2 block">Gestión de costos base.</span>
              </p>
            </div>
          </button>

          {/* Vista TODOS: red de seguridad. Combos, servicios, tipos importados raros
              y cualquier product_type inesperado siempre caen acá. */}
          <button
            onClick={() => setViewMode('all')}
            className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all group text-left relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-500"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform">
                <Layers size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight mb-2">Todos</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed">
                Ver el catálogo completo, incluidos combos y productos sin tipo definido.
                <br /><span className="text-emerald-600 font-bold text-xs mt-2 block">Si no encontrás algo, buscalo acá.</span>
              </p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // ---------- Render Principal (Tabla) ----------
  // La vista 'all' muestra las mismas columnas que terminados (precio, margen, etc.).
  const showFinishedCols = viewMode !== 'raw';
  const visibleColumnCount = showFinishedCols ? 11 : 7;

  return (
    <div className="w-full h-full p-5 md:p-6 animate-nova-in relative" translate="no">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setViewMode(null);
              setSeleccionados([]);
              setSearchTerm('');
              setRowEditModeId(null); // Limpiar edición individual al volver
            }}
            className="w-10 h-10 bg-white border border-slate-200 rounded-2xl flex items-center justify-center shadow-sm hover:text-blue-600 transition-colors"
            title="Volver al selector"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md">
            {viewMode === 'finished' ? <ChefHat size={18} className="text-[#3FF2AA]" />
              : viewMode === 'all' ? <Layers size={18} className="text-[#3FF2AA]" />
              : <Package size={18} className="text-[#3FF2AA]" />}
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
              {viewMode === 'finished' ? 'Productos Terminados' : viewMode === 'all' ? 'Todos los productos' : 'Materias Primas'}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {viewMode === 'finished' ? 'Pricing y Rentabilidad' : viewMode === 'all' ? 'Catálogo completo' : 'Costos e Insumos'}
            </p>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => onNavigate?.('gestion-costos-actualizaciones')}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Wand2 size={14} /> Actualización masiva
          </button>

          <button
            onClick={exportCSV}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Download size={14} /> Exportar
          </button>

          <button
            onClick={() => onNavigate?.('gestion-costos-importar')}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <UploadCloud size={14} /> Importar
          </button>

          <button
            onClick={() => {
              if (isEditMode) fetchAll();
              setIsEditMode(!isEditMode);
              setRowEditModeId(null); // Limpiar edición individual si tocamos la global
              setExpandedId(null);
              setKpiPanel({ open: false, type: null });
            }}
            className={`px-5 py-2.5 rounded-2xl shadow-md hover:shadow-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2
              ${isEditMode ? 'bg-slate-800 text-white' : 'bg-[#3FF2AA] text-[#0B2A36] hover:brightness-105'}`}
          >
            {isEditMode ? (
              <>
                <X size={14} /> Cancelar
              </>
            ) : (
              <>
                <Edit3 size={14} /> Editar
              </>
            )}
          </button>

          {isEditMode && (
            <button
              onClick={saveBulk}
              disabled={saving}
              className={`relative px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60
                ${saveSuccess ? 'scale-[1.02]' : ''}`}
            >
              {saving ? (
                <RefreshCcw size={14} className="animate-spin" />
              ) : saveSuccess ? (
                <Check size={16} className="text-[#0B2A36]" />
              ) : (
                <Save size={14} />
              )}
              
              {saveSuccess ? 'Guardado!' : 'Guardar cambios'}
            </button>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Valorización</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">$ {money(stats.valorizacion)}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Mg. real prom.</p>
            <TrendingUp size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{pct(stats.avgMgReal)}</p>
        </div>

        <button
          type="button"
          onClick={() => setKpiPanel((p) => ({ open: !(p.open && p.type === 'no_cost'), type: 'no_cost' }))}
          className="text-left bg-white rounded-2xl border border-slate-200 shadow-sm p-3 hover:bg-slate-50/40 transition-all"
        >
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sin costo asignado</p>
            <AlertTriangle size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${stats.sinCosto > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
            {stats.sinCosto}
          </p>
        </button>

        <button
          type="button"
          onClick={() => setKpiPanel((p) => ({ open: !(p.open && p.type === 'rent'), type: 'rent' }))}
          className="text-left bg-white rounded-2xl border border-slate-200 shadow-sm p-3 hover:bg-slate-50/40 transition-all"
        >
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Alertas rent.</p>
            <BadgeAlert size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${stats.alertasRent > 0 ? 'text-rose-700' : 'text-slate-800'}`}>
            {stats.alertasRent}
          </p>
        </button>
      </div>

      {/* KPI PANEL (detalle) */}
      {kpiPanel?.open ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                {kpiPanel.type === 'no_cost' ? 'Productos sin costo' : 'Detalle alertas rentabilidad'}
              </p>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                {seleccionados.length ? 'Basado en selección' : 'Basado en vista filtrada'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setKpiPanel({ open: false, type: null })}
              className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-slate-50 text-slate-500 border border-slate-100 hover:text-slate-700 transition-all flex items-center gap-2"
            >
              <X size={14} /> Cerrar
            </button>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {sorted
              .filter((r) => {
                if (kpiPanel.type === 'no_cost') return r.flags.costFaltante;
                return r.flags.margenNegativo || r.flags.costFaltante || r.mgReal < 5;
              })
              .slice(0, 40)
              .map((r) => (
                <button
                  key={`kpi-${kpiPanel.type}-${r.product_id}`}
                  type="button"
                  onClick={() => {
                    setExpandedId(r.product_id);
                    const el = document.getElementById('gc-listado');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-left px-3 py-2 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-slate-100/50 transition-all"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase text-slate-800 break-words whitespace-normal leading-tight mb-1">{r.productName}</div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 break-words whitespace-normal leading-tight">
                        {r.categoryName} • Costo $ {money(r.cost_price)} • Venta $ {money(r.price)}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-300 shrink-0" />
                  </div>
                </button>
              ))}
          </div>
        </div>
      ) : null}

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
          {/* BUSCADOR */}
          <div className="md:col-span-3">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                placeholder="NOMBRE, SKU O CÓDIGO."
              />
            </div>
          </div>

          {/* MARCA */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Tag size={12} /> Marca
            </label>
            <select
              value={brandFilter}
              onChange={(e) => {
                setBrandFilter(e.target.value);
                setSeleccionados([]);
                setExpandedId(null);
                setRowEditModeId(null);
              }}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-sm"
            >
              <option value="">Todas</option>
              {brandOptions.map((brand) => (
                <option key={brand.value} value={brand.value}>
                  {brand.label}
                </option>
              ))}
            </select>
          </div>

          {/* TIPO */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Layers size={12} /> Tipo
            </label>
            <select
              value={filtroTipo}
              onChange={(e) => {
                setFiltroTipo(e.target.value);
                setSeleccionados([]);
                setExpandedId(null);
                setRowEditModeId(null);
              }}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-sm"
            >
              <option value="">Todos</option>
              {productTypeOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* CATEGORÍA */}
          <div className="md:col-span-3 relative" ref={categoryMenuRef}>
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                 <Layers size={12} /> Categoría
             </label>
             <button
               type="button"
               onClick={() => setShowCategoryMenu(!showCategoryMenu)}
               className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 outline-none hover:border-[#51A7FD] transition-all flex items-center justify-between shadow-sm"
             >
                <span className="truncate">
                    {selectedCategories.length === 0 ? 'Todas' : `${selectedCategories.length} Sel.`}
                </span>
                <ChevronDown size={14} className="text-slate-400" />
             </button>

             {showCategoryMenu && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-2 border-b border-slate-50">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Filtrar por categoría</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-1">
                        {categoriesList.map(cat => {
                            const isSelected = selectedCategories.includes(cat);
                            return (
                                <div 
                                    key={cat} 
                                    onClick={() => {
                                      setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
                                      setSeleccionados([]);
                                      setExpandedId(null);
                                      setRowEditModeId(null);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors"
                                >
                                    {isSelected 
                                        ? <CheckSquare size={14} className="text-[#3FF2AA]" fill="#0B2A36" /> 
                                        : <Square size={14} className="text-slate-300" />
                                    }
                                    <span className={`text-[10px] font-bold uppercase ${isSelected ? 'text-slate-800' : 'text-slate-500'}`}>
                                        {cat}
                                    </span>
                                </div>
                            );
                        })}
                        {categoriesList.length === 0 && <p className="px-4 py-3 text-[10px] italic text-slate-300">Sin categorías</p>}
                    </div>
                    {selectedCategories.length > 0 && (
                        <div className="p-2 border-t border-slate-50 bg-slate-50/50">
                            <button 
                                type="button"
                                onClick={() => {
                                  setSelectedCategories([]);
                                  setSeleccionados([]);
                                  setExpandedId(null);
                                  setRowEditModeId(null);
                                }}
                                className="w-full py-1 text-[9px] font-black text-rose-500 uppercase hover:bg-rose-50 rounded-lg"
                            >
                                Limpiar Filtros
                            </button>
                        </div>
                    )}
                </div>
             )}
          </div>

          {/* SITIO */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <MapPin size={12} /> Sitio
            </label>
            <select
              value={filtroSitio}
              onChange={(e) => {
                setFiltroSitio(e.target.value);
                setSeleccionados([]);
                setExpandedId(null);
                setRowEditModeId(null);
              }}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-sm"
            >
              <option value="global">Global (Todos)</option>
              {sitios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Resultados: <span className="text-slate-600">{sorted.length}</span>
            {' • '}Cargados: <span className="text-slate-600">{rows.length}</span>
            {' • '}Página: <span className="text-slate-600">{safePage}/{totalPages}</span>
            {seleccionados.length ? (
              <> • Seleccionados: <span className="text-slate-600">{seleccionados.length}</span></>
            ) : null}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) || 200)}
              className="px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-white text-slate-500 border border-slate-200 hover:text-slate-700 transition-all whitespace-nowrap"
            >
              <option value={100}>100 por página</option>
              <option value={200}>200 por página</option>
              <option value={500}>500 por página</option>
            </select>

            <button
              type="button"
              onClick={() => setMostrarArchivados((v) => !v)}
              title="Los productos archivados no se venden ni se actualizan solos, pero pueden seguir usándose en recetas."
              className={`px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all whitespace-nowrap ${
                mostrarArchivados
                  ? 'bg-amber-400 text-[#0B2A36] border-amber-300'
                  : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'
              }`}
            >
              {mostrarArchivados ? 'Ocultar archivados' : 'Mostrar archivados'}
              {!mostrarArchivados && archivadosOcultos > 0 ? ` (${archivadosOcultos})` : ''}
            </button>

            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setFiltroTipo('');
                setBrandFilter('');
                setFiltroSitio('global');
                setSelectedCategories([]); 
                setSeleccionados([]);
                setExpandedId(null);
                setRowEditModeId(null);
                setKpiPanel({ open: false, type: null });
              }}
              className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-slate-50 text-slate-400 border border-slate-100 hover:text-slate-600 transition-all whitespace-nowrap"
            >
              Limpiar
            </button>

            <button
              type="button"
              onClick={() => toggleSelectAll()}
              className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                seleccionados.length === sorted.length && sorted.length > 0
                  ? 'bg-[#0B2A36] text-white shadow-md'
                  : 'bg-slate-50 text-slate-400 border border-slate-100 hover:text-slate-600'
              }`}
            >
              {seleccionados.length === sorted.length && sorted.length > 0 ? 'Quitar selección' : 'Seleccionar todo'}
            </button>
          </div>
        </div>
      </div>

      {/* LISTADO */}
      <div id="gc-listado" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado ({sorted.length})
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
              {isEditMode ? 'Edición global activa' : 'Vista'}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-4 py-4 w-10 text-center">
                  <button onClick={toggleSelectAll} className="bg-transparent p-1 rounded-lg">
                    {seleccionados.length === sorted.length && sorted.length > 0 ? (
                      <CheckSquare size={16} className="text-[#51A7FD]" />
                    ) : (
                      <Square size={16} className="text-slate-300" />
                    )}
                  </button>
                </th>
                
                <SortableTh label="Producto" sortKey="productName" />
                <SortableTh label="Marca" sortKey="brand" />
                <SortableTh label="Tipo" sortKey="productType" />
                <SortableTh label="Stock" sortKey="currentStock" align="center" />
                <SortableTh label="Costo unit." sortKey="cost_price" align="right" />
                
                {showFinishedCols && <SortableTh label={<>Margen<br/>estimado</>} sortKey="target_margin" align="center" />}
                {showFinishedCols && <SortableTh label="Sugerido" sortKey="suggestedPrice" align="right" />}
                {showFinishedCols && <SortableTh label="Venta" sortKey="price" align="right" />}
                {showFinishedCols && <SortableTh label="Mg. real" sortKey="mgReal" align="center" />}
                
                <th className="px-4 py-4 w-12"></th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {paginatedRows.map((r) => {
                const selected = seleccionados.includes(r.product_id);
                const expanded = expandedId === r.product_id;
                
                const isRowBeingEdited = isEditMode || rowEditModeId === r.product_id;

                const stockTone = r.flags.stockNeg ? 'text-rose-600' : r.currentStock === 0 ? 'text-amber-700' : 'text-slate-700';
                const marginPill = r.flags.margenNegativo ? 'bg-rose-50 text-rose-700 border-rose-100' : r.mgReal < 5 ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100';

                return (
                  <React.Fragment key={r.product_id}>
                    <tr 
                      className={`hover:bg-slate-50/60 transition-all cursor-pointer group ${selected ? 'bg-blue-50/30' : r.archivado ? 'bg-amber-50/40' : ''}`} 
                      onClick={() => {
                        if (expanded) {
                           setExpandedId(null);
                           setRowEditModeId(null); 
                        } else {
                           setExpandedId(r.product_id);
                        }
                      }}
                    >
                      <td className="px-4 py-4 text-center" onClick={(e) => { e.stopPropagation(); toggleSelect(r.product_id); }}>
                        {selected ? <CheckSquare size={16} className="text-[#51A7FD]" /> : <Square size={16} className="text-slate-200 group-hover:text-slate-300" />}
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-[12px] font-black text-slate-800 tracking-tight leading-none">
                            {r.productName}
                            {r.archivado && (
                              <span
                                title="Producto archivado: no se vende ni se actualiza automáticamente."
                                className="ml-2 px-2 py-0.5 rounded-lg text-[9px] font-black border bg-amber-50 text-amber-700 border-amber-200"
                              >
                                archivado
                              </span>
                            )}
                            {!r.variant_id && <span className="ml-2 px-2 py-0.5 rounded-lg text-[9px] font-black border bg-blue-50 text-blue-700 border-blue-100">sin var</span>}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {r.brand || 'Sin marca'}
                        </span>
                      </td>

                      <td className="px-4 py-4"><span className="text-[10px] font-black uppercase tracking-widest text-slate-600">{r.productType}</span></td>
                      <td className="px-4 py-4 text-center"><span className={`text-[12px] font-black ${stockTone}`}>{money(r.currentStock)}</span></td>

                      <td className="px-4 py-4 text-right">
                        {isRowBeingEdited && (!r.hasRecipe || viewMode === 'raw') ? (
                          <input type="number" step="0.01" value={r.cost_price} onClick={(e) => e.stopPropagation()} onChange={(e) => handleInputChange(r.product_id, 'cost_price', e.target.value)} className="w-24 bg-slate-50 border border-slate-100 rounded-xl p-1.5 text-right text-[11px] font-black text-slate-700 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD]" />
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">$ {money(r.cost_price)}</span>
                        )}
                      </td>

                      {showFinishedCols && <>
                        <td className="px-4 py-4 text-center">
                          {isRowBeingEdited ? <input type="number" step="1" value={r.target_margin} onClick={(e) => e.stopPropagation()} onChange={(e) => handleInputChange(r.product_id, 'target_margin', e.target.value)} className="w-16 bg-slate-50 border border-slate-100 rounded-xl p-1.5 text-center text-[11px] font-black text-slate-700 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD]" /> : <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{pct(r.target_margin)}</span>}
                        </td>
                        <td className="px-4 py-4 text-right"><span className="text-[10px] font-black uppercase tracking-widest text-slate-700">$ {money(r.suggestedPrice)}</span></td>
                        <td className="px-4 py-4 text-right">
                          {isRowBeingEdited ? <input type="number" step="0.01" value={r.price} onClick={(e) => e.stopPropagation()} onChange={(e) => handleInputChange(r.product_id, 'price', e.target.value)} className="w-28 bg-slate-50 border border-slate-100 rounded-xl p-1.5 text-right text-[11px] font-black text-slate-900 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD]" /> : <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">$ {money(r.price)}</span>}
                        </td>
                        <td className="px-4 py-4 text-center"><span className={`inline-flex items-center px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${marginPill}`}>{pct(r.mgReal)}</span></td>
                      </>}

                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {rowEditModeId === r.product_id ? (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveOne(rows.find(x => x.product_id === r.product_id)).then(() => setRowEditModeId(null));
                                }}
                                className="px-3 py-1.5 rounded-xl bg-[#3FF2AA] text-[#0B2A36] text-[9px] font-black uppercase tracking-widest shadow-sm hover:brightness-105"
                              >
                                Guardar
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchAll();
                                  setRowEditModeId(null);
                                }}
                                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[9px] font-black uppercase tracking-widest shadow-sm hover:bg-slate-50"
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedId(r.product_id);
                                setRowEditModeId(r.product_id);
                              }}
                              className="px-3 py-1.5 rounded-xl bg-[#3FF2AA] text-[#0B2A36] text-[9px] font-black uppercase tracking-widest shadow-sm hover:brightness-105"
                            >
                              Editar
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(expanded ? null : r.product_id);
                              if (expanded) setRowEditModeId(null);
                            }}
                            className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
                          >
                            <ChevronRight className={`text-slate-300 group-hover:text-slate-500 transition-all ${expanded ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail */}
                    {expanded && (
                      <tr className="bg-slate-50/40">
                        <td colSpan={visibleColumnCount} className="px-4 py-4">
                          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="text-[10px] font-black uppercase text-slate-500">
                              Detalle rápido: {r.productName}
                            </div>
                            <div className="flex gap-2">
                              {r.hasRecipe && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); onNavigate?.('recetas'); }} 
                                  className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-[10px] font-black uppercase hover:bg-purple-200 transition-colors flex items-center gap-1 shadow-sm"
                                  title="Ir a gestionar esta receta"
                                >
                                  <FlaskConical size={14} /> Ver Receta
                                </button>
                              )}

                              <div className="px-4 py-2 bg-[#E9FFF6] text-[#0B2A36] rounded-xl text-[10px] font-black uppercase flex items-center gap-1 shadow-sm">
                                <Edit3 size={14} /> {rowEditModeId === r.product_id ? 'Edición rápida activa' : 'Abrí el editor desde la fila'}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={visibleColumnCount} className="py-16 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.25em]">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {sorted.length > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Mostrando {((safePage - 1) * pageSize) + 1} - {Math.min(safePage * pageSize, sorted.length)} de {sorted.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={safePage === 1}
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-500 disabled:opacity-40"
              >
                <ChevronsLeft size={14} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-500 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <div className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600">
                Página {safePage} / {totalPages}
              </div>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-500 disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showHistory && (
        <div className="fixed inset-0 z-[100] bg-[#F2F9FF] w-full h-full flex flex-col animate-nova-in">
          <div className="w-full h-full flex flex-col overflow-hidden">
            <div className="bg-[#0B2A36] px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <History className="text-[#3FF2AA]" size={24} />
                <div>
                  <h2 className="text-lg font-black text-white uppercase tracking-tight">Historial de Cambios</h2>
                  <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest">Auditoría de precios y costos</p>
                </div>
              </div>
              <button onClick={() => setShowHistory(false)} className="bg-white/10 hover:bg-white/20 p-2 rounded-xl text-white transition-all"><X size={20} /></button>
            </div>
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Desde</label>
                <input type="date" value={historyFilters.dateFrom} onChange={e => setHistoryFilters({ ...historyFilters, dateFrom: e.target.value })} className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD]" />
              </div>
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Hasta</label>
                <input type="date" value={historyFilters.dateTo} onChange={e => setHistoryFilters({ ...historyFilters, dateTo: e.target.value })} className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 outline-none focus:border-[#51A7FD]" />
              </div>
              <button
                onClick={fetchHistory}
                disabled={historyLoading}
                className={`px-4 py-2 rounded-xl shadow-md text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all
                  ${filterSuccess ? 'bg-emerald-500 text-white scale-105' : 'bg-[#3FF2AA] text-[#0B2A36] hover:bg-[#3FF2AA]/80'}
                `}
              >
                {historyLoading ? <RefreshCcw size={14} className="animate-spin" /> : filterSuccess ? <Check size={14} /> : <Filter size={14} />}
                {historyLoading ? 'Buscando...' : filterSuccess ? 'Listo!' : 'Filtrar'}
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50/30 p-4">
              {historyData.length > 0 ? (
                historyData.map((log) => (
                  <div key={log.id} className="p-3 mb-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center">
                     <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-black text-slate-800 uppercase tracking-tight">{log.productName}</p>
                          <span className="px-2 py-0.5 rounded-md bg-slate-100 text-[9px] font-bold text-slate-500 border border-slate-200 uppercase">
                            {translateField(log.field_name)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-600 font-medium">
                          <span className="text-rose-500 line-through mr-2">{formatHistoryValue(log.field_name, log.from_value)}</span>
                          <span className="text-slate-300 mr-2">➜</span>
                          <span className="text-emerald-600 font-bold">{formatHistoryValue(log.field_name, log.to_value)}</span>
                        </p>
                     </div>
                     <div className="text-right mt-2 sm:mt-0 flex flex-col items-end">
                        <div className="flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                          <User size={10} className="text-slate-400" /> {log.userName}
                        </div>
                        <p className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider font-bold">
                          {new Date(log.changed_at).toLocaleDateString()} • {new Date(log.changed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                     </div>
                  </div>
                ))
              ) : (
                !historyLoading && (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <History size={32} className="mb-2 opacity-20" />
                    <p className="text-[10px] font-bold uppercase tracking-widest">No se encontraron movimientos en este rango</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
